import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ComponentDefinition, FormDocument} from '@formbird/types';
import {ConfigService} from '@services/config/config.service';
import {FormbirdInjectorService} from '@services/injector/formbird-injector.service';
import {BehaviorSubject} from 'rxjs';
import {SclAceEditorService} from '../../../../formbird-sc-shared/src/lib/scl-ace-editor.service';
import {SclAceTextRulesInterfaceService} from '../../../../formbird-sc-shared/src/lib/scl-ace-text-rules-interface.service';
import {AbstractComponent} from '../../../../formbird-sc-shared/src/lib/components/abstract.component';
import {ChangedDocumentService, NotificationService} from 'formbird-services';
import {MatTooltip} from '@angular/material/tooltip';
import {ComponentSetupService} from '../../../../formbird-sc-shared/src/lib/component-setup.service';
import {AceEditorComponent} from '../../../../ace-edit-wrapper/src/ace-edit-wrapper.component';

// extend the ComponentDefinition type with the additional fields for the component
interface AceTextComponentDefinition extends ComponentDefinition {
  scriptEditMode?: string;
  scriptEditTheme?: string;
  textFormat?: string;
  showGutter?: boolean;
  finaliseValue?: any;
  isWrapMode?: any;
  fontSize?: any;
  liveUpdates?: any;
  viMode?: any;
  aceTextOptions?: any;
}

@Component({
  templateUrl: './ace-text.component.html',
  styleUrls: ['./ace-text.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AceTextComponent extends AbstractComponent<AceTextComponentDefinition> implements AfterViewInit, OnInit {

  // element
  @ViewChild('elem', {static: false}) aceElement;
  // aceEditor;
  @ViewChild('editor', {static: false}) aceEditor: AceEditorComponent;
  allowTrigger = true;
  isFieldValueChanged = false;
  theme: string;
  mode: string;
  oldValue;

  aceSession: any;
  tempKeys;
  controlWidth;

  watchDocument: BehaviorSubject<FormDocument>;

  loggedInUserService: any;
  user: any;
  private liveUpdates: boolean;

  constructor(
    protected formbirdInjectorService: FormbirdInjectorService,
    protected changedDocumentService: ChangedDocumentService,
    private configService: ConfigService,
    private sclAceEditorService: SclAceEditorService,
    private sclAceTextRulesInterfaceService: SclAceTextRulesInterfaceService,
    protected cdr: ChangeDetectorRef,
    protected componentSetupService: ComponentSetupService,
    private notificationService: NotificationService
  ) {
    super(formbirdInjectorService, changedDocumentService, componentSetupService, cdr);
    this.loggedInUserService = this.formbirdInjectorService.get('LoggedInUserService');
  }

  ngOnInit() {
    this.componentSetupService.setupStandardProperties(this);
    this.user = this.loggedInUserService.user;
    if (typeof this.componentDefinition.scriptEditMode === 'undefined'
      || this.componentDefinition.scriptEditMode === null
      || this.componentDefinition.scriptEditMode.trim() === ''
    ) {
      this.mode = 'javascript';
    } else {
      this.mode = this.componentDefinition.scriptEditMode;
    }
    if (typeof this.componentDefinition.liveUpdates === 'undefined' || this.componentDefinition.liveUpdates === null) {
      this.liveUpdates = true;
    }else {
      this.liveUpdates = this.componentDefinition.liveUpdates;
    }

    this.theme = this.componentDefinition.scriptEditTheme?.trim() ?? 'merbivore';

    this.setupChangeDetection();

    const updateSub = this.broadcastService.on('document:updated').subscribe((docs) => {
      const doc = docs[0];
      if (doc?.documentId === this.document.documentId) {
        let content = doc[this.fieldName];
        if (typeof content === 'object') {
          content = JSON.stringify(content, undefined, 2);
        }
        this.updateValueToEditor(content);
      }
    });
    this.subs.add(updateSub);

  }

  onFieldValueChanged(fieldValue) {
    if (this.fieldValue === fieldValue) {
      return;
    }

    setTimeout(() => {
      this.fieldValue = fieldValue;
      console.assert(this.aceEditor.getEditor() !== undefined, 'ace editor should not be undefined')
      this.updateValueToEditor(fieldValue);
    });
  }

  setFieldActivation() {
    setTimeout(() => {
      const parentElement = this.aceElement.nativeElement.parentElement;
      if (this.disabled) {
        this.aceElement.nativeElement.classList.add('disabled');
        this.aceElement.nativeElement.classList.add('readonly');
        if (parentElement) {
          parentElement.classList.add('disabled');
          parentElement.classList.add('readonly');
        }

      } else {
        this.aceElement.nativeElement.classList.remove('disabled');
        this.aceElement.nativeElement.classList.remove('readonly');
        if (parentElement) {
          parentElement.classList.remove('disabled');
          parentElement.classList.remove('readonly');
        }
      }

      try {
        const div = document.getElementById('gblAceText' + this.fieldName);
        if (div) {
          const lst = div.getElementsByTagName('button');
          // tslint:disable-next-line:prefer-for-of
          for (let i = 0; i < lst.length; i++) {
            const aceEditorElementButton = lst[i] as HTMLButtonElement;
            if (this.disabled) {
              aceEditorElementButton.setAttribute('disabled', this.disabled.toString());
              aceEditorElementButton.style.cursor = 'not-allowed';
            } else {
              aceEditorElementButton.removeAttribute('disabled');
              aceEditorElementButton.style.cursor = 'pointer';
            }
          }
          if (this.aceEditor) {
            this.aceEditor.getEditor().setReadOnly(this.disabled);
          }
        }

        this.detectChanges();

      } catch (e) {
        console.error(`****************** error on settingReadOnly  *******************\n${e}`);
      }
    });

  }

  onChange(value) {
    if (!this.liveUpdates) {
      console.warn('liveUpdates is turn-off. The document fieldValue is not updated by the ace-editor:(textChanged event) and the page is not dirtied.')
    } else {
      this.valueChanged(value);
   }
  }

  // ACE
  ngAfterViewInit() {
    this.init();

    this.watchDocument = new BehaviorSubject<FormDocument>(this.document);
    this.subs.add(
      this.watchDocument.subscribe(obj => {
        this.watchDocumentFieldName(obj[this.fieldName]);
      })
    );
  }

  init() {

    if (this.document[this.fieldName]) {
      const content = this.document[this.fieldName];
      if (typeof content === 'object') {
        this.aceEditor.value = JSON.stringify(content, undefined, 2);
      } else {
        this.aceEditor.value = content;
      }
    }

    const editor = this.aceEditor.getEditor();

    editor.commands.addCommand({
      name: 'showOtherCompletions',
      bindKey: 'Ctrl-.',
      // tslint:disable-next-line:no-shadowed-variable
      exec() {
      }
    });

    this.aceSession = editor.session;
    // this needs to be an arrow function because otherwise this.configService in configureWorker is undefined
    this.aceSession.on('changeMode', (e, session) => {
      this.configureWorker(e, session)
    });

    if (this.componentDefinition.viMode || this.user.account.viMode) {
      this.aceEditor.switchToViMode(() => {
        const documentSaveService = this.formbirdInjectorService.get('DocumentSaveService');
        documentSaveService.save(this.formParameters.unsavedDocumentListId);
      })
    }

    const editorOptions = {
      minLines: 10,
      maxLines: Infinity,
      enableSnippets: true,
      wrap: true,
      enableBasicAutocompletion: true,
      enableLiveAutocompletion: true,
      autoScrollEditorIntoView: true,
      showLineNumbers: true,
      fontSize: 18,
      ...this.componentDefinition.aceTextOptions,
    };

    editorOptions.fontSize = (this.componentDefinition.fontSize) ? this.componentDefinition.fontSize : 18;

    if (this.componentDefinition.isWrapMode) {
      editorOptions.wrap = true;
    }

    editor.setOptions(editorOptions);
    editor.setReadOnly(this.disabled);

    try {
      this.sclAceEditorService.enableFullscreen(editor, this.fieldName);
    } catch (fullscreenerror) {
      console.error(`****************** error on setting fullscreen  *******************:\n${fullscreenerror}`);
    }

    // remove ace editor key binding of 'pageup' and 'pagedown'
    const keyBinding = editor.keyBinding;
    // @ts-expect-error this field is not in the bindings
    const defaultHandler = keyBinding.$defaultHandler;
    const commandKeyBinding = defaultHandler.commandKeyBinding;
    for (const key in commandKeyBinding) {
      if (key === 'pageup' || key === 'pagedown') {
        delete commandKeyBinding[key];
      }
    }

    this.tempKeys = [];

    this.setModeEdit(editor);
    this.setTheme(editor);

    const gutter = (this.componentDefinition.showGutter) ? this.componentDefinition.showGutter : true;
    editor.renderer.setShowGutter(gutter);

    // Process editor contents to document on saving
    const compDef = {...this.componentDefinition};
    compDef.finaliseValue = function () {
      this.allowTrigger = true;
      if (!this.componentDefinition.liveUpdates) {
        this.sclAceTextRulesInterfaceService.extractValuesToDoc(this.document,
          this.fieldName,
          this.componentDefinition,
          this.aceEditor,
          this.tempKeys);
      }
    }.bind(this);
    this.componentDefinition = compDef;

    this.controlWidth = this.getControlWidth(this.componentDefinition.fullWidth, null);
  }

  setModeEdit(editor) {
    const session = editor.session;
    if (typeof this.componentDefinition.scriptEditMode === 'undefined'
      || this.componentDefinition.scriptEditMode === null
      || this.componentDefinition.scriptEditMode.trim() === ''
    ) {
      // default mode is javascript
      session.setMode('ace/mode/javascript');
    } else if (this.componentDefinition.scriptEditMode === 'nools') {
      session.setMode('ace/mode/jade');
    } else {
      session.setMode(`ace/mode/${this.componentDefinition.scriptEditMode}`);
    }
  }

  setTheme(editor) {
    if (this.componentDefinition.scriptEditTheme) {
      editor.setTheme('ace/theme/' + this.componentDefinition.scriptEditTheme);
    } else {
      editor.setTheme('ace/theme/twilight');
    }
  }

  getControlWidth(fullWidth, type) {
    if (typeof type === 'undefined') {
      if (fullWidth && fullWidth === true) {
        return 'col-md-11';
      } else {
        return 'col-md-9 col-lg-8';
      }
    } else if (type === 'Date') {
      // Special case with single directive date because there is an element that occupies
      // an extra space on the column
      if (fullWidth && fullWidth === true) {
        return 'col-md-10';
      } else {
        return 'col-md-8 col-lg-7';
      }
    }
  }

  configureWorker(e, session) {
    if (session.getMode().$id === 'ace/mode/javascript') {
      if (session.$worker) {
        session.$worker.send('setOptions', [this.configService.clientConfig().jshintConfig]);
      }
    }
  }

  valueChanged(value) {
    if (this.componentDefinition.textFormat === 'raw-json' && value) {
      try {
        value = JSON.parse(value)
        super.valueChanged(value);
      } catch (e) {
        this.notificationService.error(`Invalid JSON in sc-ace-text (${this.fieldName}): ${e}`)
      }
    } else {
      super.valueChanged(value);
    }
  }

  updateValueToEditor(value) {
    const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    if(this.aceEditor){
      this.aceEditor.setText(text);
    }
  }

  watchDocumentFieldName(newValue) {
    if (!this.allowTrigger) {
      return;
    }
    if (typeof newValue === 'string') {
      if (newValue !== this.oldValue) {
        this.updateValueToEditor(newValue);
      }
    } else {
      newValue = JSON.stringify(newValue, undefined, 2);
      if (this.oldValue) {
        this.oldValue = JSON.stringify(this.oldValue, undefined, 2);
      }
      if (newValue !== this.oldValue) {
        this.updateValueToEditor(newValue);
      }
    }
    this.oldValue = newValue;
  }

  showToolTip(tooltip: any, formFieldContainer: HTMLDivElement, labelContainer: any) {
    return (labelContainer.offsetWidth <= formFieldContainer.scrollWidth);
  }

  toggleTooltip(tooltip: MatTooltip) {
    setTimeout(() => {
      tooltip.toggle();
    });
  }
}
